import { useState, useEffect, useCallback, useRef } from "react";
import {
  FiPlus,
  FiClock,
  FiX,
  FiCheck,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiSettings,
  FiPackage,
  FiSave,
  FiLink,
  FiShare2,
} from "react-icons/fi";
import utils from "../../helpers/utils";
import appointment from "../../services/appointment";
import linksthree from "../../services/linksthree";
import { Appointment, LinkThreeData } from "../../types";
import { buildInviteCadastroUrl } from "../../helpers/cadastroAlunoValidation";
import NewAppointmentModal from "../../components/appointments/NewAppointmentModal";
import { AppointmentItem } from "../../components/appointments/AppointmentItem";
import AvailabilityTab, {
  type AvailabilityTabHandle,
} from "../../components/profile/AvailabilityTab";
import ServiceTab, {
  type ServiceTabHandle,
} from "../../components/profile/ServiceTab";
import LinksManagementModal from "../../components/profile/LinksManagementModal";
import InviteLinkCard from "../../components/invite/InviteLinkCard";
import { useAuth } from "../../context/AuthContext";
import CustomCalendar from "../../components/ui/CustomCalendar";
import Skeleton from "../../components/ui/Skeleton";
import { toast } from "react-toastify";
import { getCards } from "../../services/pagbank";

function Appointments() {
  const { getUser, updateLastLogin } = useAuth();
  const userId = getUser()?.id.toString() || "";
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString());
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [showPendingOnly] = useState(true);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<number | null>(
    null,
  );
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [servicesModalOpen, setServicesModalOpen] = useState(false);
  const availabilityRef = useRef<AvailabilityTabHandle>(null);
  const servicesRef = useRef<ServiceTabHandle>(null);
  const [availabilityFooter, setAvailabilityFooter] = useState({
    hasChanges: false,
    saving: false,
    hasErrors: false,
  });
  const [servicesFooter, setServicesFooter] = useState({
    hasChanges: false,
    saving: false,
    hasErrors: false,
  });
  const [linksModalOpen, setLinksModalOpen] = useState(false);
  const [linksData, setLinksData] = useState<LinkThreeData>({
    buttons: [],
    background: "#ffffff",
    color: "#000000",
    color_secondary: "#6366f1",
    minha_url: "",
  });
  const [linksLoading, setLinksLoading] = useState(false);
  const [professionalUrl, setProfessionalUrl] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [isConfigExpanded, setIsConfigExpanded] = useState(false);
  // Banner de cartão PagBank está comentado; estado mantido para reativação.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- usado no JSX comentado abaixo
  const [hasPaymentCard, setHasPaymentCard] = useState<boolean | null>(null);

  // Atualizar last_login ao acessar a página de Agenda
  useEffect(() => {
    if (userId) {
      updateLastLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when userId is available
  }, [userId]);

  // Verificar se o profissional já tem cartão cadastrado
  useEffect(() => {
    const loadCards = async () => {
      try {
        const data = await getCards();
        setHasPaymentCard((data.cards || []).length > 0);
      } catch (error) {
        console.error("Erro ao carregar cartões PagBank:", error);
        setHasPaymentCard(null);
      }
    };
    if (userId) {
      void loadCards();
    }
  }, [userId]);

  const loadAppointments = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      if (showPendingOnly) {
        // Buscar compromissos de múltiplas semanas e filtrar apenas pendentes
        const allAppointments: Appointment[] = [];
        const today = new Date();

        // Buscar compromissos das próximas 8 semanas
        for (let i = 0; i < 8; i++) {
          const weekDate = new Date(today);
          weekDate.setDate(today.getDate() + i * 7);
          try {
            const weekData = await appointment.getWeekAppointment(
              userId,
              weekDate,
            );
            allAppointments.push(...weekData);
          } catch (error) {
            console.error(`Erro ao carregar semana ${i}:`, error);
          }
        }

        // Filtrar apenas pendentes (não confirmados e não cancelados)
        const pendingAppointments = allAppointments.filter(
          (apt) => apt.status !== "confirmed" && apt.status !== "cancelled",
        );

        // Ordenar por data (usando timezone de São Paulo)
        pendingAppointments.sort((a, b) => {
          return (
            utils.toSaoPauloTimeZone(a.start_time).getTime() -
            utils.toSaoPauloTimeZone(b.start_time).getTime()
          );
        });

        setAppointments(pendingAppointments);
      } else {
        // Buscar apenas compromissos do dia selecionado
        const data = await appointment.getDayAppointment(
          userId,
          new Date(selectedDate),
        );
        setAppointments(data);
      }
    } catch (error) {
      console.error("Erro ao carregar compromissos:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedDate, showPendingOnly]);

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedDate, showPendingOnly]);

  const handleCancelAppointment = (appointmentId: number) => {
    setAppointmentToCancel(appointmentId);
    setShowCancelModal(true);
  };

  const confirmCancelAppointment = async () => {
    if (appointmentToCancel) {
      await appointment.cancelAppointment(String(appointmentToCancel), userId);
      setShowCancelModal(false);
      setAppointmentToCancel(null);
      loadAppointments();
    }
  };

  const handleConfirmAppointment = async (appointmentId: number) => {
    await appointment.confirmAppointment(String(appointmentId), userId);
    loadAppointments();
  };

  const handleEditAppointment = (apt: Appointment) => {
    setEditData(apt);
    setOpenModal(true);
  };

  const loadLinksData = useCallback(async () => {
    if (!userId) return;
    try {
      const result = await linksthree.getButtons(Number(userId));
      setLinksData(result);
      const slug = result?.minha_url || userId;
      setProfessionalUrl(`${window.location.origin}/s/${slug}`);
      setInviteUrl(buildInviteCadastroUrl(window.location.origin, result?.minha_url, userId));
    } catch (error) {
      console.error("Erro ao carregar dados de links:", error);
    }
  }, [userId]);

  const handleSaveLinks = async (tempData: LinkThreeData) => {
    setLinksLoading(true);
    try {
      const response = await linksthree.putButton(
        Number(userId),
        tempData.buttons || [],
      );

      const responseColors = await linksthree.putColors(Number(userId), {
        background: tempData.background,
        color: tempData.color,
        color_secondary: tempData.color_secondary,
        minha_url: tempData.minha_url ?? null,
      });

      if (response && responseColors.success) {
        setLinksData(tempData);
        const slug = tempData.minha_url || userId;
        setProfessionalUrl(`${window.location.origin}/s/${slug}`);
        setInviteUrl(
          buildInviteCadastroUrl(window.location.origin, tempData.minha_url, userId),
        );
        toast.success("Links e cores salvos com sucesso!");
        setLinksModalOpen(false);
        setLinksLoading(false);
        return true;
      }
      toast.error(
        responseColors.error || "Erro ao salvar algumas informações.",
      );
      setLinksLoading(false);
      return false;
    } catch (error) {
      console.error("Erro ao salvar links:", error);
      toast.error("Erro ao salvar links.");
      setLinksLoading(false);
      return false;
    }
  };

  const handleOpenLinksModal = () => {
    loadLinksData();
    setLinksModalOpen(true);
  };

  const handleShareProfessionalLink = () => {
    if (!professionalUrl) return;

    if (navigator.share) {
      navigator
        .share({
          title: "Meu Site Profissional",
          text: "Confira meu site profissional!",
          url: professionalUrl,
        })
        .catch((error) => {
          console.error("Erro ao compartilhar:", error);
        });
    } else {
      navigator.clipboard.writeText(professionalUrl);
      toast.success("Link copiado para área de transferência!");
    }
  };

  useEffect(() => {
    loadLinksData();
  }, [loadLinksData]);

  const confirmedCount = appointments.filter(
    (apt) => apt.status === "confirmed",
  ).length;
  const pendingCount = appointments.filter(
    (apt) => apt.status !== "confirmed" && apt.status !== "cancelled",
  ).length;
  const cancelledCount = appointments.filter(
    (apt) => apt.status === "cancelled",
  ).length;

  return (
    <div className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 2xl:px-10">
      <InviteLinkCard inviteUrl={inviteUrl} />

      {/* Header Premium */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="truncate bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-3xl font-bold text-transparent dark:from-purple-400 dark:via-pink-400 dark:to-blue-400 sm:text-4xl">
            Agenda
          </h2>
          <p className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Gerencie seus compromissos e disponibilidade
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:gap-3">
          <button
            onClick={() => {
              setEditData(null);
              setOpenModal(true);
            }}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl sm:w-auto sm:rounded-xl sm:px-6 sm:py-3 sm:text-base"
          >
            <FiPlus size={18} />
            <span className="whitespace-nowrap">Novo Compromisso</span>
          </button>
          <br />
          <div className="w-full sm:w-auto">
            {/* Título clicável para expandir/colapsar */}
            <button
              type="button"
              onClick={() => setIsConfigExpanded(!isConfigExpanded)}
              className="inline-flex w-full items-center justify-between gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2.5 text-[9px] font-semibold text-white shadow-lg shadow-blue-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl sm:w-auto sm:rounded-xl sm:px-5 sm:py-3 sm:text-base"
            >
              <div className="flex items-center gap-2">
                <FiSettings size={13} />
                <span className="whitespace-nowrap">Configurar Agenda</span>
              </div>
              {isConfigExpanded ? (
                <FiChevronUp size={13} className="text-white/80" />
              ) : (
                <FiChevronDown size={13} className="text-white/80" />
              )}
            </button>

            {/* Botões colapsáveis */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isConfigExpanded
                  ? "max-h-96 opacity-100 mt-2"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setAvailabilityModalOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-purple-500 bg-white px-4 py-2.5 text-xs font-semibold text-purple-600 shadow-sm transition-all duration-300 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 hover:text-white hover:shadow-lg dark:border-purple-500 dark:bg-gray-800 dark:text-purple-400 dark:hover:bg-gradient-to-r dark:hover:from-purple-600 dark:hover:to-pink-600 dark:hover:text-white sm:rounded-xl sm:px-5 sm:py-3 sm:text-base"
                >
                  <FiSettings size={15} />
                  <span className="whitespace-nowrap">
                    Gerenciar Minha Disponibilidade
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setServicesModalOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-500 bg-white px-4 py-2.5 text-xs font-semibold text-blue-600 shadow-sm transition-all duration-300 hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-600 hover:text-white hover:shadow-lg dark:border-blue-500 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-gradient-to-r dark:hover:from-blue-600 dark:hover:to-cyan-600 dark:hover:text-white sm:rounded-xl sm:px-5 sm:py-3 sm:text-base"
                >
                  <FiPackage size={15} />
                  <span className="whitespace-nowrap">
                    Gerenciar Serviços Prestados
                  </span>
                </button>
                <div className="flex w-full gap-2">
                  <button
                    type="button"
                    onClick={handleOpenLinksModal}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-pink-500 bg-white px-4 py-2.5 text-xs font-semibold text-pink-600 shadow-sm transition-all duration-300 hover:bg-gradient-to-r hover:from-pink-600 hover:to-rose-600 hover:text-white hover:shadow-lg dark:border-pink-500 dark:bg-gray-800 dark:text-pink-400 dark:hover:bg-gradient-to-r dark:hover:from-pink-600 dark:hover:to-rose-600 dark:hover:text-white sm:rounded-xl sm:px-5 sm:py-3 sm:text-base"
                  >
                    <FiLink size={15} />
                    <span className="whitespace-nowrap">
                      Gerenciar Meu Site
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleShareProfessionalLink}
                    disabled={!professionalUrl}
                    className="inline-flex items-center justify-center rounded-lg border-2 border-pink-500 bg-white px-3 py-2.5 text-pink-600 shadow-sm transition-all duration-300 hover:bg-gradient-to-r hover:from-pink-600 hover:to-rose-600 hover:text-white hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 dark:border-pink-500 dark:bg-gray-800 dark:text-pink-400 dark:hover:bg-gradient-to-r dark:hover:from-pink-600 dark:hover:to-rose-600 dark:hover:text-white sm:rounded-xl sm:px-4 sm:py-3"
                    title="Compartilhar meu site"
                  >
                    <FiShare2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Aviso de plano de testes / cartão não cadastrado */}
      {/* {hasPaymentCard === false && (
        <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-3 text-xs text-yellow-800 shadow-sm sm:px-4 sm:py-3 sm:text-sm dark:border-yellow-600/60 dark:bg-yellow-900/30 dark:text-yellow-100">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <FiAlertTriangle className="h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-semibold">
                  Você está em um plano de testes.
                </p>
                <p className="mt-0.5">
                  Cadastre um cartão de crédito para continuar usando a agenda sem interrupções.
                </p>
              </div>
            </div>
            <div className="sm:ml-auto">
              <Link
                to="/pagamento"
                className="inline-flex items-center justify-center rounded-full bg-yellow-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-yellow-700 sm:px-4 sm:py-2"
              >
                Cadastrar cartão
              </Link>
            </div>
          </div>
        </div>
      )} */}

      {/* Stats Cards */}
      <div className="mb-3 grid grid-cols-3 gap-1.5 sm:mb-6 sm:gap-4">
        {loading ? (
          <>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="rounded-md border border-stroke bg-white p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-4 md:p-5"
              >
                <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-3">
                  <Skeleton className="h-6 w-6 rounded-md sm:h-10 sm:w-10 md:h-12 md:w-12" />
                  <div className="flex flex-col items-center gap-0.5 sm:items-start sm:gap-1">
                    <Skeleton className="h-2 w-10 sm:h-3 sm:w-16 md:h-4 md:w-24" />
                    <Skeleton className="h-4 w-6 sm:h-6 sm:w-10 md:h-8 md:w-16" />
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="rounded-md border border-stroke bg-white p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-4 md:p-5">
              <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-3">
                <div className="rounded-md bg-green-100 p-1 dark:bg-green-900/30 sm:rounded-lg sm:p-2 md:p-3">
                  <FiCheck
                    className="text-green-600 dark:text-green-400"
                    size={12}
                  />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-medium leading-tight text-gray-600 dark:text-gray-400 sm:text-xs">
                    Confirmados
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-white sm:text-xl md:text-2xl">
                    {confirmedCount}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-stroke bg-white p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-4 md:p-5">
              <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-3">
                <div className="rounded-md bg-yellow-100 p-1 dark:bg-yellow-900/30 sm:rounded-lg sm:p-2 md:p-3">
                  <FiClock
                    className="text-yellow-600 dark:text-yellow-400"
                    size={12}
                  />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-medium leading-tight text-gray-600 dark:text-gray-400 sm:text-xs">
                    Pendentes
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-white sm:text-xl md:text-2xl">
                    {pendingCount}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-stroke bg-white p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-4 md:p-5">
              <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-3">
                <div className="rounded-md bg-red-100 p-1 dark:bg-red-900/30 sm:rounded-lg sm:p-2 md:p-3">
                  <FiX className="text-red-600 dark:text-red-400" size={12} />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-medium leading-tight text-gray-600 dark:text-gray-400 sm:text-xs">
                    Cancelados
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-white sm:text-xl md:text-2xl">
                    {cancelledCount}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 xl:grid-cols-12">
        {/* Calendar Section - Minimizável no Mobile */}
        <div className="xl:col-span-5">
          <div className="rounded-lg border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl md:rounded-2xl">
            <button
              onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
              className="flex w-full items-center gap-2 border-b border-stroke px-3 py-3 transition-colors hover:bg-gray-50 dark:border-strokedark dark:hover:bg-gray-800/50 sm:cursor-default sm:px-5 sm:py-4 md:px-7 md:py-5 xl:pointer-events-none"
            >
              <div className="rounded-lg bg-purple-100 p-1.5 dark:bg-purple-900/30 sm:p-2">
                <FiCalendar
                  className="text-purple-600 dark:text-purple-400"
                  size={16}
                />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <h3 className="truncate text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                  Calendário
                </h3>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {isCalendarExpanded
                    ? "Selecione uma data"
                    : utils.formatDate(new Date(selectedDate))}
                </p>
              </div>
              <div className="flex-shrink-0 xl:hidden">
                {isCalendarExpanded ? (
                  <FiChevronUp className="text-gray-400" size={20} />
                ) : (
                  <FiChevronDown className="text-gray-400" size={20} />
                )}
              </div>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isCalendarExpanded
                  ? "max-h-none opacity-100"
                  : "max-h-0 opacity-0"
              } xl:max-h-none xl:opacity-100`}
            >
              <div className="p-3 sm:p-5 md:p-7">
                <div className="overflow-hidden rounded-lg border border-stroke bg-gray-50 p-2 dark:border-strokedark dark:bg-meta-4 sm:p-3 md:p-4">
                  <CustomCalendar
                    onChange={(date) => setSelectedDate(date.toISOString())}
                    value={selectedDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Appointments List Section */}
        <div className="xl:col-span-7">
          <div className="rounded-lg border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl md:rounded-2xl">
            <div className="flex flex-col gap-2 border-b border-stroke px-3 py-3 dark:border-strokedark sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-5 sm:py-4 md:px-7 md:py-5">
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                <div className="flex-shrink-0 rounded-lg bg-blue-100 p-1.5 dark:bg-blue-900/30 sm:p-2">
                  <FiClock
                    className="text-blue-600 dark:text-blue-400"
                    size={16}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                    {showPendingOnly ? "Pendentes" : "Compromissos do Dia"}
                  </h3>
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {showPendingOnly
                      ? "Todos os dias"
                      : utils.formatDate(new Date(selectedDate))}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2.5 sm:justify-end sm:gap-3">
                <span className="flex-shrink-0 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 sm:px-3 sm:text-sm">
                  {appointments.length}
                </span>
              </div>
            </div>

            <div className="p-3 sm:p-4 md:p-6 sm:max-h-[calc(100vh-22rem)] sm:overflow-auto lg:max-h-[70vh]">
              {loading ? (
                <div className="flex flex-col gap-2.5 sm:gap-3 md:gap-4">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-stroke bg-gray-50 p-2.5 dark:border-strokedark dark:bg-meta-4 sm:rounded-xl sm:p-3.5 md:p-5"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                        <div className="flex items-start gap-2.5 sm:gap-3 md:gap-4">
                          <Skeleton className="h-10 w-10 flex-shrink-0 rounded-lg sm:h-12 sm:w-12 md:h-14 md:w-14" />
                          <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-2">
                            <Skeleton className="h-4 w-24 sm:h-4 sm:w-28 md:h-5 md:w-32" />
                            <Skeleton className="h-3 w-20 sm:h-3 sm:w-24 md:h-4 md:w-28" />
                            <Skeleton className="h-5 w-16 rounded-full sm:h-5 sm:w-18 md:h-6 md:w-20" />
                          </div>
                        </div>
                        <div className="flex gap-1.5 self-end sm:gap-2 sm:self-auto">
                          <Skeleton className="h-9 w-9 rounded-lg" />
                          <Skeleton className="h-9 w-9 rounded-lg" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : appointments.length > 0 ? (
                <div className="flex flex-col gap-2.5 sm:gap-3 md:gap-4">
                  {appointments.map((apt: Appointment) => (
                    <AppointmentItem
                      key={apt.id}
                      appointment={apt}
                      allAppointments={appointments}
                      onEdit={handleEditAppointment}
                      onConfirm={handleConfirmAppointment}
                      onCancel={handleCancelAppointment}
                      showActions={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-12 px-4 dark:border-gray-700 sm:rounded-xl sm:py-16 md:py-20">
                  <div className="rounded-full bg-gradient-to-br from-purple-100 to-blue-100 p-4 dark:from-purple-900/30 dark:to-blue-900/30 sm:p-5 md:p-6">
                    <FiClock
                      className="text-gray-400 dark:text-gray-600"
                      size={28}
                    />
                  </div>
                  <p className="mt-4 text-center text-base font-semibold text-gray-700 dark:text-gray-300 sm:mt-5 sm:text-lg md:text-xl">
                    Nenhum compromisso
                  </p>
                  <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-500 sm:mt-2.5 sm:text-sm">
                    Clique em "Novo Compromisso" para agendar
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Novo Compromisso */}
      <NewAppointmentModal
        isOpen={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditData(null);
        }}
        onSuccess={() => {
          setOpenModal(false);
          setEditData(null);
          loadAppointments();
        }}
        initialData={editData}
      />

      {/* Modal Agenda (disponibilidade) - mobile first */}
      {availabilityModalOpen && (
        <div
          className="fixed inset-0 z-[999999] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAvailabilityModalOpen(false);
          }}
        >
          <div
            className="relative flex max-h-screen h-full w-full flex-col overflow-hidden rounded-t-2xl border-t border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark sm:max-w-4xl sm:rounded-2xl sm:border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-stroke px-3 py-3 dark:border-strokedark sm:px-6 sm:py-4">
              <h3 className="text-base font-bold text-gray-900 dark:text-white sm:text-lg">
                Agenda (disponibilidade)
              </h3>
              <button
                type="button"
                onClick={() => setAvailabilityModalOpen(false)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                aria-label="Fechar"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-6">
              <AvailabilityTab
                ref={availabilityRef}
                hideIndicators
                hideSaveButton
                onStateChange={setAvailabilityFooter}
              />
            </div>
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-stroke bg-white px-3 py-3 dark:border-strokedark dark:bg-boxdark sm:flex-row sm:justify-end sm:gap-3 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={() => setAvailabilityModalOpen(false)}
                className="rounded-lg border border-stroke px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => availabilityRef.current?.save()}
                disabled={
                  !availabilityFooter.hasChanges ||
                  availabilityFooter.saving ||
                  availabilityFooter.hasErrors
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6 sm:py-3"
              >
                {availabilityFooter.saving ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FiSave size={18} />
                    Salvar alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Serviços - mobile first */}
      {servicesModalOpen && (
        <div
          className="fixed inset-0 z-[999999] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) setServicesModalOpen(false);
          }}
        >
          <div
            className="relative flex max-h-screen h-full w-full flex-col overflow-hidden rounded-t-2xl border-t border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark sm:max-w-3xl sm:rounded-2xl sm:border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-stroke px-3 py-3 dark:border-strokedark sm:px-6 sm:py-4">
              <h3 className="text-base font-bold text-gray-900 dark:text-white sm:text-lg">
                Serviços
              </h3>
              <button
                type="button"
                onClick={() => setServicesModalOpen(false)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                aria-label="Fechar"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-6">
              <ServiceTab
                ref={servicesRef}
                hideIndicators
                hideFooter
                onStateChange={setServicesFooter}
              />
            </div>
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-stroke bg-white px-3 py-3 dark:border-strokedark dark:bg-boxdark sm:flex-row sm:justify-end sm:gap-3 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={() => setServicesModalOpen(false)}
                className="rounded-lg border border-stroke px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => servicesRef.current?.save()}
                disabled={
                  !servicesFooter.hasChanges ||
                  servicesFooter.saving ||
                  servicesFooter.hasErrors
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6 sm:py-3"
              >
                {servicesFooter.saving ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FiSave size={18} />
                    Salvar alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Cancelamento */}
      {showCancelModal && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-boxdark">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                <FiX className="text-red-600 dark:text-red-400" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Cancelar Compromisso
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>

            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Tem certeza que deseja cancelar este compromisso?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setAppointmentToCancel(null);
                }}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-strokedark dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Não, manter
              </button>
              <button
                onClick={confirmCancelAppointment}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                Sim, cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gerenciar Meu Site */}
      <LinksManagementModal
        isOpen={linksModalOpen}
        data={linksData}
        loading={linksLoading}
        onClose={() => setLinksModalOpen(false)}
        onSave={handleSaveLinks}
      />
    </div>
  );
}

export default Appointments;
